import { memo, useState } from "react";
import "./ChatExperienceSurvey.css";
import { CHAT_REACTIONS, CHAT_REACTION_LABELS, type ChatReaction } from "~shared/chatExperienceSurvey";
import { ChatReactionIcon } from "./ChatReactionIcon";
import { cn } from "src/lib/cn";
import { Text } from "src/sudokit/components/Text";
import { SudoButton } from "src/sudokit/components/SudoButton";

export type ChatExperienceSurveyPayload = {
  reaction: ChatReaction | null;
  dontAskAgain?: boolean;
  feedbackText?: string;
};

type Props = {
  onSubmit: (payload: ChatExperienceSurveyPayload) => boolean | Promise<boolean>;
  onSkip: (dontAskAgain: boolean) => boolean | Promise<boolean>;
  disabled?: boolean;
};

export const ChatExperienceSurvey = memo(({ onSubmit, onSkip, disabled }: Props) => {
  const [reaction, setReaction] = useState<ChatReaction | null>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDisabled = disabled || isSubmitting;

  const handleSubmit = async () => {
    if (isDisabled) return;
    setIsSubmitting(true);
    const trimmed = feedbackText.trim();
    const submitted = await onSubmit({ reaction, feedbackText: trimmed || undefined, dontAskAgain });
    if (!submitted) setIsSubmitting(false);
  };

  const handleSkip = async (optOut = dontAskAgain) => {
    if (isDisabled) return;
    setIsSubmitting(true);
    const submitted = await onSkip(optOut);
    if (!submitted) setIsSubmitting(false);
  };

  return (
    <div
      data-testid="chat-experience-survey"
      className="w-full rounded-[12px] border border-app-menu-color-hover bg-app-menu-color shadow-card overflow-hidden mb-1"
    >
      <div className="px-4 py-3 flex flex-col gap-3" style={{ containerType: "inline-size", containerName: "chat-survey" }}>
        <div className="flex flex-col gap-2">
          <Text className="text-sm font-semibold text-app-grey-0">
            How's chat doing so far?
          </Text>
          <div role="radiogroup" aria-label="Rate your chat experience" className="flex flex-nowrap gap-1.5">
            {CHAT_REACTIONS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={reaction === option}
                disabled={isDisabled}
                onClick={() => setReaction(option)}
                className={cn(
                  "inline-flex items-center justify-center whitespace-nowrap gap-1.5 rounded-lg border px-2 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-cta-high-contrast",
                  reaction === option
                    ? "border-app-primary-cta-high-contrast bg-app-primary-cta-high-contrast text-white"
                    : "border-app-menu-color-hover bg-app-document-background text-app-grey-0 hover:bg-app-menu-color-hover",
                  isDisabled && "cursor-not-allowed opacity-50",
                )}
              >
                <span className="chat-survey-reaction-icon"><ChatReactionIcon reaction={option} /></span>
                {CHAT_REACTION_LABELS[option]}
              </button>
            ))}
          </div>
        </div>

        <textarea
          value={feedbackText}
          onChange={(event) => setFeedbackText(event.target.value)}
          placeholder="What worked well, or what could be better? (optional)"
          rows={3}
          disabled={isDisabled}
          className={cn(
            "w-full resize-none rounded-lg border border-app-menu-color-hover bg-app-document-background",
            "px-3 py-2 text-sm text-app-grey-0 placeholder:text-app-grey-2",
            "focus:outline-none focus:ring-1 focus:ring-app-primary-cta-high-contrast",
            isDisabled && "opacity-50 cursor-not-allowed",
          )}
        />

        <label className="flex items-center gap-2 self-start text-xs text-app-grey-1 cursor-pointer">
          <input
            type="checkbox"
            checked={dontAskAgain}
            onChange={(event) => {
              const checked = event.target.checked;
              setDontAskAgain(checked);
              if (checked) void handleSkip(true);
            }}
            disabled={isDisabled}
            className="h-4 w-4 shrink-0 cursor-pointer disabled:cursor-not-allowed"
          />
          <span>Don't ask again this session</span>
        </label>

        <div className="flex items-center justify-end gap-2">
          <SudoButton
            variant="tertiaryGrey"
            size="small"
            disabled={isDisabled}
            onClick={() => void handleSkip()}
          >
            Not now
          </SudoButton>
          <SudoButton
            variant="primary"
            size="small"
            disabled={isDisabled}
            onClick={handleSubmit}
          >
            {isSubmitting ? "Sending..." : "Submit"}
          </SudoButton>
        </div>
      </div>
    </div>
  );
});

ChatExperienceSurvey.displayName = "ChatExperienceSurvey";
